<script setup lang="ts">
import {h, computed} from 'vue'
import {useStore} from 'vuex'
import {ColumnDef} from '@tanstack/vue-table'
import {ArrowUpDown, NotepadTextDashed, Lock} from 'lucide-vue-next'

import {formatTime} from '@/utils/datetime'

import {Project, ReportTemplate} from '@/types'
import {DataTable} from '@/components/ui/data-table'
import {Button} from '@/components/ui/button'
import TemplateDropdown from './TemplateDropdown.vue'


const store = useStore()
const settings = computed(() => store.getters['user/getUserSettings'])

const props = defineProps<{
  project: Project | null,
  templates: [ReportTemplate],
  projectId: number
}>()

const columns: ColumnDef<ReportTemplate>[] = [
  {
    accessorKey: 's3_icon',
    header: 'Icon',
    cell: ({row}) => {
      if (row.getValue('s3_icon') === null) {
        return h(NotepadTextDashed, {class: 'w-7 h-7 text-muted-foreground'})
      }
      return h('img', {class: 'w-8 h-8 object-contain', src: row.getValue('s3_icon')})
    },
  },
  {
    accessorKey: 'name',
    header: ({column}) => {
      return h(Button, {
        variant: 'ghost',
        size: 'sm',
        onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
      }, () => ['Name', h(ArrowUpDown, {class: 'h-3 w-3'})])
    },
    cell: ({row}) => {
      const isSystem = row.original.is_system_template
      return h('div', {
        class: 'flex items-center gap-2',
        'data-testid': `template-name-${row.original.id}`
      }, [
        isSystem ? h(Lock, { class: 'h-4 w-4 text-blue-600' }) : null,
        h('span', { class: 'text-foreground' }, row.getValue('name'))
      ])
    }
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    cell: ({row}) => {
      const date = formatTime(row.getValue('created_at'), settings.value.tz, 'D MMMM YYYY')
      return h('div', {class: 'text-muted-foreground whitespace-nowrap'}, date)
    },
  },
  {
    accessorKey: 'color',
    header: '',
    cell: ({row}) => {
      return h('div', {class: 'w-4 h-4 rounded-full', style: `background-color: ${row.getValue('color')}`})
    },
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      const template = row.original
      return h('div', { class: 'relative' }, h(TemplateDropdown, {
        project_id: props.projectId,
        template: template,
      }))
    },
  }
]
</script>

<template>
  <DataTable
    :columns="columns"
    :data="templates"
    :initial-sorting="[{ id: 'created_at', desc: true }]"
  />
</template>
